import { useEffect } from 'react'
import {
  DefaultValues,
  FieldValues,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from 'react-hook-form'
import {
  Accordion,
  CustomButton,
  TableActionType,
  TableColumnType,
} from '@ui-kit'
import { TAB_INDEX } from '../constants/form.constants'
import PersistTable from './PersistTable'

interface AccordionFormWithTableProps<TForm extends FieldValues, TRow> {
  formHeading: string
  tableHeading?: string
  tableName?: string
  selectedRow: TRow | null
  defaultValues: DefaultValues<TForm>
  onSubmit: (data: TForm | null) => void
  columns?: TableColumnType<TRow>[]
  data?: TRow[]
  actions?: TableActionType<TRow>[]
  renderFormFields: (
    methods: UseFormReturn<TForm>,
    selectedRow: TRow | null
  ) => React.ReactNode
  addLabel: string
  updateLabel: string
  cancelLabel: string
  disabledKey?: keyof TRow
  tableClassName?: string
  tableMinHeight?: string
  isTableDefaultActive?: boolean
  showTableAccordion?: boolean
}

const AccordionFormWithTable = <TForm extends FieldValues, TRow>({
  formHeading,
  tableHeading = '',
  tableName = '',
  selectedRow,
  defaultValues,
  onSubmit,
  columns = [],
  data = [],
  actions,
  renderFormFields,
  addLabel,
  updateLabel,
  cancelLabel,
  disabledKey,
  tableClassName = 'bg-none p-[0.25rem]',
  tableMinHeight = '10rem',
  isTableDefaultActive = true,
  showTableAccordion = true,
}: AccordionFormWithTableProps<TForm, TRow>) => {
  const methods = useForm<TForm>({
    defaultValues,
  })
  const { handleSubmit, reset } = methods

  useEffect(() => {
    if (selectedRow) {
      reset(selectedRow as TForm)
      return
    }

    reset(defaultValues)
  }, [selectedRow, reset, defaultValues])

  const onFormSubmit: SubmitHandler<TForm> = (data) => {
    onSubmit(data)
    reset(defaultValues)
  }

  const handleCancel = () => {
    onSubmit(null)
    reset(defaultValues)
  }

  return (
    <>
      <Accordion
        heading={formHeading}
        tabIndex={TAB_INDEX}
        className="bg-none"
        borderColor="secondary-500"
        isDisabled={false}
        triggerOpen={!!selectedRow}
      >
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <div className="flex flex-col gap-[0.75rem] p-[0.75rem]">
            {renderFormFields(methods, selectedRow)}
            <div className="flex justify-end gap-[0.75rem]">
              <CustomButton
                color="primary"
                type="button"
                onClick={handleCancel}
              >
                {cancelLabel}
              </CustomButton>
              <CustomButton
                color="primary"
                isFilled={true}
                type="button"
                onClick={handleSubmit(onFormSubmit)}
              >
                {selectedRow ? updateLabel : addLabel}
              </CustomButton>
            </div>
          </div>
        </form>
      </Accordion>

      {showTableAccordion && (
        <Accordion
          heading={tableHeading}
          tabIndex={TAB_INDEX}
          className={tableClassName}
          borderColor="secondary-500"
          isDisabled={false}
          isDefaultActive={isTableDefaultActive}
        >
          <div className="table-wrap flex w-full">
            <PersistTable
              tableName={tableName}
              data={data}
              columns={columns}
              actions={actions}
              disabledKey={disabledKey}
              emptyDataMessage="No record found."
              minHeight={tableMinHeight}
              isPersistColumns={false}
              emptyDataMessageHeight="min-h-32 max-h-32"
            />
          </div>
        </Accordion>
      )}
    </>
  )
}

export default AccordionFormWithTable
